import { generateHTML, generateJSON, type JSONContent } from '@tiptap/core';
import { inlineTranslationExtensions } from './inline-translation-extensions';
import { translationPlainText, type TiptapBlock } from '@fame/shared';

export type TranslationFragment = { key: string; text: string };

/** Keep semantic units intact while respecting the configured provider's limits. */
export function translationBatches(fragments: TranslationFragment[]) {
  const batches: TranslationFragment[][] = [];
  const oversized: string[] = [];
  let batch: TranslationFragment[] = [];
  let characters = 0;
  for (const fragment of fragments) {
    if (fragment.text.length > 5000) {
      oversized.push(fragment.key);
      continue;
    }
    if (batch.length === 100 || characters + fragment.text.length > 45000) {
      batches.push(batch);
      batch = [];
      characters = 0;
    }
    batch.push(fragment);
    characters += fragment.text.length;
  }
  if (batch.length) batches.push(batch);
  return { batches, oversized };
}

export function translationFragmentHTML(block: TiptapBlock): string {
  return generateHTML({ type: 'doc', content: [block] }, inlineTranslationExtensions);
}

/** Parse defensively before Tiptap can silently discard unsupported markup. */
export function translatedInlineBlock(html: string, source: TiptapBlock): TiptapBlock {
  const dom = new DOMParser().parseFromString(html, 'text/html');
  if (dom.head.childNodes.length) throw new Error('De vertaling bevat niet-ondersteunde inhoud buiten het tekstblok.');
  const nodes = [...dom.body.childNodes].filter(node => node.nodeType !== 3 || node.textContent?.trim());
  const attributes = source.attrs;
  const level = attributes && typeof attributes === 'object' && 'level' in attributes ? attributes.level : 2;
  const tag = source.type === 'heading' ? `H${level ?? 2}` : 'P';
  const root = nodes[0];
  if (nodes.length !== 1 || !(root instanceof Element) || root.tagName !== tag)
    throw new Error('Onverwachte blokstructuur: verwacht één overeenkomstige alinea of kop.');
  const allowed: Record<string, true> = {
    STRONG: true,
    B: true,
    EM: true,
    I: true,
    S: true,
    STRIKE: true,
    U: true,
    CODE: true,
    SPAN: true,
    A: true,
    BR: true,
  };
  for (const element of root.querySelectorAll('*')) {
    if (!allowed[element.tagName]) throw new Error('De vertaling bevat niet-ondersteunde inhoud.');
    for (const attribute of element.attributes) {
      if (element.tagName === 'A' && ['href', 'target', 'rel', 'class'].includes(attribute.name)) continue;
      if (
        element.tagName === 'SPAN' &&
        attribute.name === 'style' &&
        /^\s*color\s*:\s*(#[0-9a-f]{3,8}|rgb\([\d\s,]+\));?\s*$/i.test(attribute.value)
      )
        continue;
      throw new Error('De vertaling bevat niet-ondersteunde attributen.');
    }
  }
  for (const attribute of root.attributes) {
    if (attribute.name !== 'style' || !/^\s*color\s*:\s*(#[0-9a-f]{3,8}|rgb\([\d\s,]+\));?\s*$/i.test(attribute.value))
      throw new Error('De vertaling bevat gewijzigde blokattributen.');
  }
  const document: JSONContent = generateJSON(html, inlineTranslationExtensions);
  const blocks = document.content ?? [];
  if (blocks.length !== 1 || blocks[0].type !== source.type) throw new Error('Onverwachte blokstructuur.');
  const content = blocks[0].content ?? [];
  if (!translationPlainText({ content }).trim()) throw new Error('De vertaling is leeg.');
  const sourceLinks = inlineLinks(source.content);
  const targetLinks = inlineLinks(content);
  if (
    sourceLinks.length !== targetLinks.length ||
    sourceLinks.some((link, index) => link.href !== targetLinks[index].href)
  )
    throw new Error('De vertaling heeft bronlinks verwijderd of gewijzigd.');
  let linkIndex = 0;
  let previousHref: unknown;
  for (const node of content) {
    for (const mark of node.marks ?? []) {
      if (mark.type !== 'link') continue;
      if (mark.attrs?.href !== previousHref) linkIndex++;
      previousHref = mark.attrs?.href;
      mark.attrs = { ...sourceLinks[linkIndex - 1] };
    }
    if (!(node.marks ?? []).some(mark => mark.type === 'link')) previousHref = undefined;
  }
  return { ...source, content };
}

function inlineLinks(content: unknown): Array<Record<string, unknown>> {
  const links: Array<Record<string, unknown>> = [];
  let previousHref: unknown;
  for (const node of (Array.isArray(content) ? content : []) as Array<{
    marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
  }>) {
    const mark = node.marks?.find(item => item.type === 'link');
    if (mark && mark.attrs?.href !== previousHref) links.push(mark.attrs ?? {});
    previousHref = mark?.attrs?.href;
  }
  return links;
}
